import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  Breadcrumbs,
  Link,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Stack,
  Snackbar,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  DriveFolderUpload as UploadFolderIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';

import {
  listChunks,
  getChunkUploadUrl,
  deleteChunk,
  deleteAllChunks,
  triggerReindex,
  listReindexRuns,
  getReindexRun,
  putChunkWithProgress,
} from '../../api/parcelPipeline';

const formatFileSize = (bytes) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m${s}s`;
};

const POLL_INTERVAL_MS = 5000;

const STATUS_CHIP_PROPS = {
  queued: { label: 'queued', color: 'default', icon: <HourglassIcon /> },
  processing: { label: 'running', color: 'primary', icon: <SyncIcon /> },
  completed: { label: 'completed', color: 'success', icon: <CheckCircleIcon /> },
  failed: { label: 'failed', color: 'error', icon: <ErrorIcon /> },
};

const StatusChip = ({ status }) => {
  const props = STATUS_CHIP_PROPS[status] || STATUS_CHIP_PROPS.queued;
  return <Chip size="small" {...props} />;
};


// ── Source chunks panel ────────────────────────────────────────────────── //

function SourceChunksPanel({ onChange }) {
  const [chunks, setChunks] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadQueue, setUploadQueue] = useState([]); // [{file, relativePath, progress, error}]
  const fileInputRef = useRef(null);
  const dirInputRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listChunks();
      setChunks(data.chunks || []);
      setTotalSize(data.total_size || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to list chunks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSelectFiles = () => fileInputRef.current?.click();
  const handleSelectDirectory = () => dirInputRef.current?.click();

  const handleFiles = async (event) => {
    // Pulled from <input type=file>: file.webkitRelativePath is set on
    // directory uploads (webkitdirectory attribute) and empty otherwise.
    // Strip the top-level directory the user picked — the user expects
    // their selection to map to s3://parcels-bucket/all/, not
    // s3://parcels-bucket/all/<picked-dir>/.
    const rawFiles = Array.from(event.target.files || []);
    event.target.value = ''; // allow re-selecting the same dir later

    const entries = rawFiles
      .map((file) => {
        const rel = file.webkitRelativePath || file.name;
        // Drop the first path segment when a directory was picked. For a
        // plain file input webkitRelativePath is empty so this is a no-op.
        const stripped = file.webkitRelativePath
          ? rel.split('/').slice(1).join('/') || file.name
          : rel;
        return { file, relativePath: stripped };
      })
      .filter((e) => e.relativePath.toLowerCase().endsWith('.parquet'));

    if (!entries.length) {
      if (rawFiles.length) {
        setError('No .parquet files found in the selection.');
      }
      return;
    }

    // Initialise queue rows so the user sees progress bars immediately.
    setUploadQueue((q) => [
      ...q,
      ...entries.map(({ file, relativePath }) => ({
        file,
        relativePath,
        progress: 0,
        error: null,
        done: false,
      })),
    ]);

    for (const { file, relativePath } of entries) {
      try {
        const { upload_url } = await getChunkUploadUrl(relativePath);
        await putChunkWithProgress(upload_url, file, (frac) => {
          setUploadQueue((q) =>
            q.map((row) => (row.file === file ? { ...row, progress: frac } : row)),
          );
        });
        setUploadQueue((q) =>
          q.map((row) =>
            row.file === file ? { ...row, progress: 1, done: true } : row,
          ),
        );
      } catch (e) {
        setUploadQueue((q) =>
          q.map((row) =>
            row.file === file
              ? {
                  ...row,
                  error:
                    e?.response?.data?.detail ||
                    e?.message ||
                    'upload failed',
                }
              : row,
          ),
        );
      }
    }
    await refresh();
    if (onChange) onChange();
    // Auto-clear completed rows after a beat so the queue doesn't keep growing.
    setTimeout(() => {
      setUploadQueue((q) => q.filter((row) => !row.done));
    }, 1500);
  };

  const handleDelete = async (relativePath) => {
    if (!window.confirm(`Delete ${relativePath}?`)) return;
    try {
      await deleteChunk(relativePath);
      await refresh();
      if (onChange) onChange();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to delete chunk');
    }
  };

  const [clearingAll, setClearingAll] = useState(false);
  const handleClearAll = async () => {
    if (!chunks.length) return;
    const msg =
      `Delete all ${chunks.length} chunk file(s) under all/?\n\n` +
      `Use this before re-uploading a freshly-rebuilt parcel dataset — ` +
      `the pipeline generates new filenames per run, so old + new files ` +
      `would otherwise coexist in S3 and double-count during reindex.`;
    if (!window.confirm(msg)) return;
    setClearingAll(true);
    setError(null);
    try {
      const res = await deleteAllChunks();
      await refresh();
      if (onChange) onChange();
      // eslint-disable-next-line no-alert
      window.alert(`Deleted ${res?.deleted ?? 0} chunk file(s).`);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to clear chunks');
    } finally {
      setClearingAll(false);
    }
  };

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">Source chunks</Typography>
          <Typography variant="body2" color="text.secondary">
            Parquet files in <code>s3://parcels-bucket/all/</code> — the reindex
            spatial-joins each chunk against per-tenant boundaries.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={refresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              chunks.length
                ? 'Delete every chunk under all/. Use before re-uploading a freshly-rebuilt dataset.'
                : 'No chunks to clear'
            }
          >
            <span>
              <Button
                variant="outlined"
                color="error"
                startIcon={
                  clearingAll ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <DeleteIcon />
                  )
                }
                onClick={handleClearAll}
                disabled={!chunks.length || clearingAll}
              >
                Clear all
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={handleSelectFiles}
          >
            Upload file(s)
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFolderIcon />}
            onClick={handleSelectDirectory}
          >
            Upload directory
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".parquet"
            multiple
            hidden
            onChange={handleFiles}
          />
          <input
            ref={dirInputRef}
            type="file"
            hidden
            // `webkitdirectory` is the de-facto attribute for picking a whole
            // directory tree; React doesn't know it so we use the lowercase
            // DOM form. `directory` and `mozdirectory` are no-ops in modern
            // Chromium/WebKit but harmless and document intent.
            webkitdirectory=""
            directory=""
            mozdirectory=""
            onChange={handleFiles}
          />
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {uploadQueue.length > 0 && (
        <Box mb={2}>
          {uploadQueue.map((row, idx) => (
            <Box key={idx} mb={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {row.relativePath}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.error
                    ? row.error
                    : row.done
                    ? 'uploaded'
                    : `${Math.round(row.progress * 100)}%`}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.round(row.progress * 100)}
                color={row.error ? 'error' : row.done ? 'success' : 'primary'}
              />
            </Box>
          ))}
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      ) : chunks.length === 0 ? (
        <Alert severity="info">
          No chunks uploaded yet. Use <strong>Upload file(s)</strong> for
          individual Parquet files or <strong>Upload directory</strong> for
          a hive-partitioned tree (e.g. <code>Region=…/Ward=…/*.parquet</code>).
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>Last modified</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {chunks.map((c) => (
                <TableRow key={c.key} hover>
                  <TableCell sx={{ wordBreak: 'break-all' }}>
                    <code>{c.relative_path || c.filename}</code>
                  </TableCell>
                  <TableCell align="right">{formatFileSize(c.size)}</TableCell>
                  <TableCell>{formatDate(c.last_modified)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(c.relative_path || c.filename)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell><strong>{chunks.length} files</strong></TableCell>
                <TableCell align="right"><strong>{formatFileSize(totalSize)}</strong></TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}


// ── Reindex trigger panel ──────────────────────────────────────────────── //

function ReindexPanel({ runs, busy, onTrigger, chunksCount }) {
  const lastRun = runs[0];
  const isRunning = busy || lastRun?.status === 'processing' || lastRun?.status === 'queued';

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6">Reindex</Typography>
          <Typography variant="body2" color="text.secondary">
            Spatial-joins every chunk against every tenant's boundaries and
            writes per-(tenant, boundary) Parquet shards to{' '}
            <code>by-fpa/</code>.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={isRunning ? <SyncIcon /> : <PlayIcon />}
          disabled={isRunning || chunksCount === 0}
          onClick={onTrigger}
        >
          {isRunning ? 'Running…' : 'Run reindex'}
        </Button>
      </Stack>

      {chunksCount === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No source chunks uploaded yet — reindex would have nothing to process.
        </Alert>
      )}

      {lastRun && (
        <Box>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <StatusChip status={lastRun.status} />
            <Typography variant="body2">
              <strong>Last run:</strong> {formatDate(lastRun.started_at)}
            </Typography>
            <Typography variant="body2">
              <strong>Chunks:</strong>{' '}
              {lastRun.completed_chunks}/{lastRun.total_chunks} done
              {lastRun.failed_chunks > 0 && (
                <> ({lastRun.failed_chunks} failed)</>
              )}
            </Typography>
            <Typography variant="body2">
              <strong>Matched:</strong> {lastRun.total_matched_parcels.toLocaleString()}
              {' / unmatched: '}{lastRun.total_unmatched_parcels.toLocaleString()}
            </Typography>
            {lastRun.duration_seconds != null && (
              <Typography variant="body2">
                <strong>Duration:</strong> {formatDuration(lastRun.duration_seconds)}
              </Typography>
            )}
          </Stack>
          {lastRun.status === 'processing' && lastRun.total_chunks > 0 && (
            <LinearProgress
              variant="determinate"
              value={
                ((lastRun.completed_chunks + lastRun.failed_chunks) /
                  lastRun.total_chunks) *
                100
              }
              sx={{ mt: 2 }}
            />
          )}
          {lastRun.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {lastRun.error}
            </Alert>
          )}
        </Box>
      )}
    </Paper>
  );
}


// ── Run history panel ──────────────────────────────────────────────────── //

function ChunkRow({ chunk }) {
  return (
    <TableRow>
      <TableCell><code>{chunk.chunk_key}</code></TableCell>
      <TableCell><StatusChip status={chunk.status} /></TableCell>
      <TableCell align="right">
        {chunk.matched_parcels != null ? chunk.matched_parcels.toLocaleString() : '—'}
      </TableCell>
      <TableCell align="right">
        {chunk.unmatched_parcels != null ? chunk.unmatched_parcels.toLocaleString() : '—'}
      </TableCell>
      <TableCell align="right">{formatDuration(chunk.duration_seconds)}</TableCell>
      <TableCell>
        {chunk.error ? (
          <Tooltip title={chunk.error}>
            <Typography variant="caption" color="error">{chunk.error.slice(0, 50)}…</Typography>
          </Tooltip>
        ) : '—'}
      </TableCell>
    </TableRow>
  );
}

function RunRow({ run }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReindexRun(run.id);
      setDetail(data);
    } finally {
      setLoading(false);
    }
  }, [run.id]);

  useEffect(() => {
    if (open) loadDetail();
  }, [open, loadDetail]);

  // Re-fetch detail every 5s while the run is live and the row is expanded.
  useEffect(() => {
    if (!open) return undefined;
    if (run.status !== 'processing' && run.status !== 'queued') return undefined;
    const id = setInterval(loadDetail, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [open, run.status, loadDetail]);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{formatDate(run.started_at)}</TableCell>
        <TableCell><StatusChip status={run.status} /></TableCell>
        <TableCell align="right">
          {run.completed_chunks}/{run.total_chunks}
          {run.failed_chunks > 0 && <span style={{ color: 'red' }}> ({run.failed_chunks} failed)</span>}
        </TableCell>
        <TableCell align="right">{run.total_matched_parcels.toLocaleString()}</TableCell>
        <TableCell align="right">{run.total_unmatched_parcels.toLocaleString()}</TableCell>
        <TableCell align="right">{formatDuration(run.duration_seconds)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Chunks ({detail?.chunks?.length ?? 0})
              </Typography>
              {loading && !detail ? (
                <CircularProgress size={20} />
              ) : detail?.chunks?.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Chunk</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Matched</TableCell>
                      <TableCell align="right">Unmatched</TableCell>
                      <TableCell align="right">Duration</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.chunks.map((c) => <ChunkRow key={c.id} chunk={c} />)}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No chunks yet.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function RunHistoryPanel({ runs, loading, onRefresh }) {
  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Run history</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={onRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      {runs.length === 0 ? (
        <Alert severity="info">No reindex runs yet — trigger one above.</Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Started</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Chunks</TableCell>
                <TableCell align="right">Matched</TableCell>
                <TableCell align="right">Unmatched</TableCell>
                <TableCell align="right">Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((r) => <RunRow key={r.id} run={r} />)}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}


// ── Page shell ─────────────────────────────────────────────────────────── //

export default function ParcelPipeline() {
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [chunksCount, setChunksCount] = useState(0);

  const refreshRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const data = await listReindexRuns(20);
      setRuns(data.runs || []);
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  const refreshChunkCount = useCallback(async () => {
    try {
      const data = await listChunks();
      setChunksCount((data.chunks || []).length);
    } catch {
      setChunksCount(0);
    }
  }, []);

  useEffect(() => {
    refreshRuns();
    refreshChunkCount();
  }, [refreshRuns, refreshChunkCount]);

  // Poll runs while any are live so the user sees fan-out converge in real time.
  const anyLive = useMemo(
    () => runs.some((r) => r.status === 'processing' || r.status === 'queued'),
    [runs],
  );
  useEffect(() => {
    if (!anyLive) return undefined;
    const id = setInterval(refreshRuns, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [anyLive, refreshRuns]);

  const handleTrigger = async () => {
    setBusy(true);
    try {
      await triggerReindex();
      setToast({ severity: 'success', message: `Reindex queued for ${chunksCount} chunk(s).` });
      await refreshRuns();
    } catch (e) {
      setToast({ severity: 'error', message: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/admin">Admin</Link>
        <Typography color="text.primary">Parcel Pipeline</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>Parcel Pipeline</Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Manage the source Parquet chunks in S3, trigger spatial reindex, and
        watch each chunk worker's progress.
      </Typography>

      <Box mt={3}>
        <SourceChunksPanel onChange={refreshChunkCount} />
        <ReindexPanel
          runs={runs}
          busy={busy}
          onTrigger={handleTrigger}
          chunksCount={chunksCount}
        />
        <RunHistoryPanel runs={runs} loading={loadingRuns} onRefresh={refreshRuns} />
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Container>
  );
}
